/**
 * Voting Widget Component
 * Displays vote counts, progress bars, and voting buttons
 */
export function VotingWidget({
  proposalId,
  votesFor,
  votesAgainst,
  votesAbstain,
  quorum,
  onVote,
  voting,
  userVote,
}) {
  const totalVotes = votesFor + votesAgainst + votesAbstain;
  const quorumReached = totalVotes >= quorum;

  // Calculate percentages
  const forPercent = totalVotes > 0 ? (votesFor / totalVotes) * 100 : 0;
  const againstPercent = totalVotes > 0 ? (votesAgainst / totalVotes) * 100 : 0;
  const abstainPercent = totalVotes > 0 ? (votesAbstain / totalVotes) * 100 : 0;

  // Check if user can vote
  const canVote = window.t24Governance.canVote;
  const hasVoted = !!userVote;

  return (
    <div className="t24-voting-widget">
      {/* Vote Counts */}
      <div className="t24-vote-counts">
        <span className="t24-vote-count-for">👍 {votesFor}</span>
        <span className="t24-vote-count-against">👎 {votesAgainst}</span>
        <span className="t24-vote-count-abstain">🤷 {votesAbstain}</span>
      </div>

      {/* Vote Progress Bar */}
      <div className="t24-vote-progress-bar">
        <div className="t24-vote-progress-fill">
          <div
            className="t24-vote-progress-for"
            style={{ width: `${forPercent}%` }}
            title={`For: ${forPercent.toFixed(1)}%`}
          />
          <div
            className="t24-vote-progress-against"
            style={{ width: `${againstPercent}%` }}
            title={`Against: ${againstPercent.toFixed(1)}%`}
          />
          <div
            className="t24-vote-progress-abstain"
            style={{ width: `${abstainPercent}%` }}
            title={`Abstain: ${abstainPercent.toFixed(1)}%`}
          />
        </div>
      </div>

      {/* Quorum Indicator */}
      <div className="t24-quorum-indicator">
        <div className="t24-quorum-bar">
          <div
            className={`t24-quorum-fill ${quorumReached ? 'reached' : ''}`}
            style={{ width: `${Math.min((totalVotes / quorum) * 100, 100)}%` }}
          />
        </div>
        <span className="t24-quorum-text">
          {totalVotes}/{quorum} ({window.t24Governance.strings.quorum})
        </span>
      </div>

      {/* Vote Buttons */}
      {canVote && (
        <div className="t24-vote-buttons">
          <button
            className={`t24-vote-btn for ${userVote === 'for' ? 'voted' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onVote('for');
            }}
            disabled={voting.voting || hasVoted}
          >
            {userVote === 'for' ? '✓ ' : ''}
            {window.t24Governance.strings.voteFor}
          </button>

          <button
            className={`t24-vote-btn against ${userVote === 'against' ? 'voted' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onVote('against');
            }}
            disabled={voting.voting || hasVoted}
          >
            {userVote === 'against' ? '✓ ' : ''}
            {window.t24Governance.strings.voteAgainst}
          </button>

          <button
            className={`t24-vote-btn abstain ${userVote === 'abstain' ? 'voted' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onVote('abstain');
            }}
            disabled={voting.voting || hasVoted}
          >
            {userVote === 'abstain' ? '✓ ' : ''}
            {window.t24Governance.strings.abstain}
          </button>
        </div>
      )}

      {!canVote && (
        <div className="t24-vote-login-prompt">
          <p>{window.t24Governance.strings.loginToVote}</p>
        </div>
      )}
    </div>
  );
}
